import { Typography } from '../typography'
import { IconAdd, IconChevronLeft, IconChevronRight } from '../icons'
import { Button } from '../../components/button'

import { ImportantNotice } from '../demo/important-notice'
import { CodeExample } from '../demo/code-example'
import { DemoTabs } from '../demo/demo-tabs'

export function ButtonDemo() {
  return (
    <div className="space-y-2">
      <Typography variant="cf-heading-01" className="text-cf-grey-900">
        Button
      </Typography>

      <DemoTabs
        tabs={{
          preview: (
            <>
              <ImportantNotice>
                The different colour variants are being defined. Set the color by passing the
                corresponding CSS variable to the `className` prop.
              </ImportantNotice>

              <div className="space-y-6">
                <div className="border-cf-grey-100 rounded-lg border p-6">
                  <Typography variant="cf-heading-02" className="text-cf-grey-900 mb-4">
                    Variants
                  </Typography>
                  <div className="space-y-4">
                    <div>
                      <div className="flex flex-wrap gap-4">
                        <Button variant="primary">Primary Button</Button>
                        <Button variant="primary" disabled>
                          Primary Disabled
                        </Button>
                        <Button variant="secondary">Secondary Button</Button>
                        <Button variant="tertiary">Tertiary Button</Button>
                        <Button variant="ghost">Ghost Button</Button>
                      </div>
                      <CodeExample
                        code={`import { Button } from '@repo/design-system-cf/components/button'

// Primary button
<Button variant="primary">Primary Button</Button>

// Disabled state
<Button variant="primary" disabled>Primary Disabled</Button>

// Secondary button
<Button variant="secondary">Secondary Button</Button>

// Tertiary button
<Button variant="tertiary">Tertiary Button</Button>

// Ghost button
<Button variant="ghost">Ghost Button</Button>
`}
                      />
                    </div>

                    <div>
                      <Typography variant="cf-heading-03" className="text-cf-grey-800 mb-2">
                        Sizes
                      </Typography>
                      <div className="flex flex-wrap items-center gap-4">
                        <Button size="small">Small</Button>
                        <Button>Default</Button>
                        <Button size="large">Large</Button>
                      </div>
                      <CodeExample
                        code={`import { Button } from '@repo/design-system-cf/components/button'

// Small size
<Button size="small">Small</Button>

// Default/Regular size
<Button>Default</Button>

// Large size
<Button size="large">Large</Button>`}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-cf-grey-100 rounded-lg border p-6">
                  <Typography variant="cf-heading-03" className="text-cf-grey-900 mb-4">
                    Icon + text
                  </Typography>
                  <div className="space-y-4">
                    <div>
                      <div className="flex flex-wrap gap-4">
                        <Button iconLeft={<IconChevronLeft />}>Previous</Button>
                        <Button iconRight={<IconChevronRight />}>Next</Button>
                        <Button iconLeft={<IconChevronLeft />} iconRight={<IconChevronRight />}>
                          Navigate
                        </Button>
                      </div>
                      <CodeExample
                        code={`import { Button } from '@repo/design-system-cf/components/button'
import { ChevronLeftIcon, ChevronRightIcon } from 'primitives/icons'

// With left icon
<Button iconLeft={<ChevronLeftIcon />}>Previous</Button>

// With right icon
<Button iconRight={<ChevronRightIcon />}>Next</Button>

// With both icons
<Button iconLeft={<ChevronLeftIcon />} iconRight={<ChevronRightIcon />}>
  Navigate
</Button>`}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-cf-grey-100 rounded-lg border p-6">
                  <Typography variant="cf-heading-03" className="text-cf-grey-900 mb-4">
                    Icon only
                  </Typography>
                  <div className="space-y-4">
                    <div>
                      <div className="space-x-4">
                        <Button size="small" iconLeft={<IconAdd />} aria-label="Add item" />
                        <Button size="regular" iconLeft={<IconAdd />} aria-label="Add item" />
                        <Button size="large" iconLeft={<IconAdd />} aria-label="Add item" />
                      </div>
                      <CodeExample
                        code={`import { Button } from '@repo/design-system-cf/components/button'
import { PlusIcon } from 'primitives/icons'

// The button will automatically become square when only an icon is provided
<Button
  size="small"
  iconLeft={<PlusIcon />}
  aria-label="Add item"
/>
<Button
  size="regular"
  iconLeft={<PlusIcon />}
  aria-label="Add item"
/>
<Button
  size="large"
  iconLeft={<PlusIcon />}
  aria-label="Add item"
/>`}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-cf-grey-100 rounded-lg border p-6">
                  <Typography variant="cf-heading-03" className="text-cf-grey-900 mb-4">
                    Full width
                  </Typography>
                  <div className="space-y-4">
                    <div>
                      <div className="flex flex-wrap gap-4 max-w-screen-sm bg-cf-grey-100 rounded p-6">
                        <Button fullWidth>Full width button</Button>
                      </div>
                      <CodeExample
                        code={`import { Button } from '@repo/design-system-cf/components/button'

// Full width button
<Button fullWidth>Full Width Button</Button>`}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-cf-grey-100 rounded-lg border p-6">
                  <Typography variant="cf-heading-03" className="text-cf-grey-900 mb-4">
                    Loading state
                  </Typography>
                  <Typography variant="body-01" className="mb-4">
                    The loading state overlays a spinner on top of the button content while keeping
                    the original content visible with reduced opacity. The button is automatically
                    disabled during loading.
                  </Typography>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center">
                        <div className="flex space-x-4">
                          <div>
                            <Button size="small" loading>
                              Loading...
                            </Button>
                          </div>
                          <div>
                            <Button loading>Loading...</Button>
                          </div>

                          <div>
                            <Button size="large" loading>
                              Loading...
                            </Button>
                          </div>
                          <div>
                            <Button
                              loading
                              iconLeft={<IconChevronLeft />}
                              iconRight={<IconChevronRight />}
                            >
                              With icons
                            </Button>
                          </div>
                        </div>
                      </div>
                      <CodeExample
                        code={`import { Button } from '@repo/design-system-cf/components/button'
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from 'primitives/icons'

// Basic loading button
<Button loading>Loading...</Button>

// Loading button with variant
<Button variant="secondary" loading>Process data</Button>

// Loading button with icons
<Button 
  loading
  iconLeft={<ChevronLeftIcon />}
  iconRight={<ChevronRightIcon />}
>
  With icons
</Button>

// Loading icon-only button
<Button 
  loading
  iconLeft={<PlusIcon />}
  aria-label="Add item"
/>`}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-cf-grey-100 rounded-lg border p-6 bg-cf-purple-900">
                  <Typography variant="cf-heading-03" className="text-cf-grey-50 mb-4">
                    Inverse Variants
                  </Typography>
                  <div className="space-y-4">
                    <div>
                      <div className="flex flex-wrap gap-4">
                        <Button variant="primary-inverse">Primary Inverse Button</Button>
                      </div>
                      <CodeExample
                        code={`import { Button } from '@repo/design-system-cf/components/button'

// Primary Inverse button
<Button variant="primary-inverse">Primary Inverse Button</Button>`}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-cf-grey-100 rounded-lg border p-6">
                  <Typography variant="cf-heading-03" className="text-cf-grey-900 mb-4">
                    Unofficial variants
                  </Typography>
                  <Typography variant="body-01" className=" mb-4">
                    These variants are already in use within the app. They have been included as
                    custom options here to simplify the eventual migration to the official design
                    system variants.
                  </Typography>
                  <div className="flex flex-wrap gap-4 mt-8">
                    <Button variant="primary-green">Primary Green Button</Button>

                    <Button variant="secondary-grey">Secondary Grey Button</Button>

                    <Button variant="tertiary-grey">Tertiary Grey Button</Button>

                    <Button variant="destructive">Destructive Button</Button>
                  </div>
                  <CodeExample
                    code={`import { Button } from '@repo/design-system-cf/components/button'

// Primary green button
<Button variant="primary-green">Primary Green Button</Button> 

// Secondary grey button
<Button variant="secondary-grey">Secondary Grey Button</Button>


// Tertiary grey button
<Button variant="tertiary-grey">Tertiary Grey Button</Button>

// Destructive button
<Button variant="destructive">Destructive Button</Button>`}
                  />
                </div>
              </div>
            </>
          ),
          api: {
            properties: [
              {
                name: 'variant',
                type: "'primary' | 'secondary' | 'tertiary' | 'ghost' | 'primary-inverse' | 'primary-green' | 'secondary-grey' | 'tertiary-grey'",
                defaultValue: "'primary'",
                description: 'The visual style of the button',
              },
              {
                name: 'size',
                type: "'regular' | 'small' | 'large'",
                defaultValue: "'regular'",
                description: 'The size of the button',
              },
              {
                name: 'disabled',
                type: 'boolean',
                defaultValue: 'false',
                description: 'Whether the button is disabled',
              },
              {
                name: 'className',
                type: 'string',
                defaultValue: 'undefined',
                description: 'Additional CSS classes to apply',
              },
              {
                name: 'iconLeft',
                type: 'ReactNode',
                defaultValue: 'undefined',
                description:
                  'Icon component to display on the left side. When used without children, the button will become square.',
              },
              {
                name: 'iconRight',
                type: 'ReactNode',
                defaultValue: 'undefined',
                description:
                  'Icon component to display on the right side. When used without children, the button will become square.',
              },
              {
                name: 'type',
                type: "'button' | 'submit' | 'reset'",
                defaultValue: "'button'",
                description: 'The button type attribute',
              },
              {
                name: 'asChild',
                type: 'boolean',
                defaultValue: 'false',
                description:
                  'Whether to merge props onto the child instead of creating a new element',
              },
              {
                name: 'fullWidth',
                type: 'boolean',
                defaultValue: 'false',
                description: 'Whether the button should take up the full width of its container',
              },
              {
                name: 'loading',
                type: 'boolean',
                defaultValue: 'false',
                description:
                  'Whether the button is in a loading state. Shows a spinner overlay and disables the button.',
              },
            ],
          },
          changelog: [
            {
              date: '11-06-2025',
              changes: [
                'Added loading state with spinner overlay. The loading state shows a spinner on top of the button content while keeping the original content visible with reduced opacity. Button is automatically disabled during loading.',
              ],
            },
            {
              date: '20-05-2025',
              changes: [
                'Added fullWidth prop and documentation. Padding for small buttons without an icon went from 10 to 8 pixels. Added "primary-inverse" button variant. Purple colors became the default primary/secondary/tertiary colors.',
              ],
            },
            {
              date: '28-04-2025',
              changes: ['Added secondary, tertiary, and ghost button variants.'],
            },
            {
              date: '24-04-2025',
              changes: [
                'Added text spacing for different sizes. Now button with text has margins to increase the space with icons.',
              ],
            },
            {
              date: '11-04-2025',
              changes: [
                'Initial version with iconLeft, iconRight, size, disabled, asChild. Colors are still under develop so custom classes are used instead',
              ],
            },
          ],
          design: (
            <iframe
              style={{ border: '1px solid rgba(0, 0, 0, 0.1)' }}
              width="100%"
              height="100%"
              src="https://embed.figma.com/proto/c8oXlEODLmj3csp3ux5DKu/Design-System---Components?node-id=318-4009&m=dev&scaling=min-zoom&content-scaling=fixed&page-id=1%3A34&embed-host=share"
              allowFullScreen
            />
          ),
        }}
      />
    </div>
  )
}
